import { BoardManifest } from "@/game/constants/boardManifest";
import { CommonManifest } from "@/game/constants/commonManifest";
import { DiceManifest } from "@/game/constants/diceManifest";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import { useGameApi } from "@/hooks/useGameApi";
import { type GameState } from "@nextgammon/shared";
import { Assets } from "pixi.js";
import { useEffect, useState } from "react";

interface GameDetailsType {
  player1Id: string;
  player2Id: string;
  player1Username?: string;
  player2Username?: string;
  status?: string;
  cubeValue?: number;
  cubeOwner?: string | null;
  pendingDoubleFrom?: string | null;
  stakes?: number;
  turnCount?: number;
  doublingAllowed?: boolean;
  currentTurn?: string | null;
  turnStartedAt?: number | null;
  turnDeadline?: number | null;
  timeRemaining?: number | null;
  gameMode: string; // Required - static but kept in type for simplicity
  timeBank1?: number | null;
  timeBank2?: number | null;
}

export function useGameLoader(gameIdFromUrl: string | undefined, userId: string | null) {
  const [loadedGameId, setLoadedGameId] = useState<string | null>(null);
  const [initialGameStateInternal, setInitialGameStateInternal] = useState<GameState | null>(null);
  const [initialGameDetailsInternal, setInitialGameDetailsInternal] = useState<GameDetailsType>({
    player1Id: "",
    player2Id: "",
    status: undefined,
    cubeValue: undefined,
    cubeOwner: undefined,
    pendingDoubleFrom: undefined,
    stakes: undefined,
    gameMode: "standard" // Default game mode
  });
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const [errorLoadingInitialData, setErrorLoadingInitialData] = useState<string | null>(null);
  const [hasJoinedGame, setHasJoinedGame] = useState(false);

  const gameApi = useGameApi();

  const [isLoadingGamePlayAssets, setIsLoadingGamePlayAssets] = useState(false);
  const [errorLoadingGamePlayAssets, setErrorLoadingGamePlayAssets] = useState(false);
  const [loadedGamePlayAssetBundles, setLoadedGamePlayAssetBundles] = useState<string[] | null>(
    null
  );

  // Effect to parse gameIdFromUrl and set loadedGameId for fetching
  useEffect(() => {
    if (gameIdFromUrl) {
      {
        setLoadedGameId(gameIdFromUrl); // A specific game ID to attempt loading
      }
    } else {
      // gameIdFromUrl is undefined, treat as no game to load
      setLoadedGameId(null);
      setInitialGameStateInternal(null);
      setInitialGameDetailsInternal({
        player1Id: "",
        player2Id: "",
        status: undefined,
        gameMode: "standard"
      });
      setErrorLoadingInitialData(null);
      setIsLoadingInitialData(false);
    }
  }, [gameIdFromUrl]);

  // Effect to fetch initial game data when loadedGameId (a specific ID) and gameApi are available
  useEffect(() => {
    if (!loadedGameId || !gameApi) {
      // If no specific game ID to load (e.g., "new" or cleared) or no token, do nothing.
      // Ensure loading state is false if there's no ID to load.
      if (!loadedGameId) {
        setIsLoadingInitialData(false);
        setInitialGameStateInternal(null); // Clear any stale data
        setInitialGameDetailsInternal({
          player1Id: "",
          player2Id: "",
          status: undefined,
          cubeValue: undefined,
          cubeOwner: undefined,
          pendingDoubleFrom: undefined,
          stakes: undefined,
          turnCount: undefined,
          gameMode: "standard"
        });
        setErrorLoadingInitialData(null);
        setHasJoinedGame(false);
      }
      if (loadedGameId && !gameApi) {
        setErrorLoadingInitialData("Authentication not available. Cannot load game.");
        setIsLoadingInitialData(false);
        setHasJoinedGame(false);
      }
      return;
    }

    let loadingTimeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;

    const clearLoadingTimeout = () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }
    };

    const fetchInitialGameData = async () => {
      if (!isMounted) return;

      GameLogger.logInfo(
        "useGameLoader",
        `Attempting to fetch initial data for game: ${loadedGameId}`
      );
      setInitialGameStateInternal(null); // Reset before fetching new game
      setInitialGameDetailsInternal({
        player1Id: "",
        player2Id: "",
        status: undefined,
        gameMode: "standard"
      });
      setErrorLoadingInitialData(null);
      setIsLoadingInitialData(true);

      loadingTimeoutId = setTimeout(() => {
        if (isMounted && isLoadingInitialData) {
          GameLogger.logInfo("useGameLoader", `Initial data loading timeout for ${loadedGameId}.`);
          setErrorLoadingInitialData(
            `Timeout: Failed to load initial details for game \"${loadedGameId}\".`
          );
          setIsLoadingInitialData(false);
        }
      }, 15000); // 15 seconds

      try {
        const gameData = await gameApi.getGame(loadedGameId);

        if (!isMounted) return;
        clearLoadingTimeout();

        GameLogger.logInfo(
          "useGameLoader",
          `Successfully fetched initial data for ${loadedGameId}:`,
          gameData
        );

        // Check if current user is the game creator (player1)
        const isGameCreator = userId && gameData.game.player1Id === userId;

        // If the game is in SETUP status, call join endpoint to register player as ready
        if (gameData.game.status === "SETUP") {
          try {
            GameLogger.logInfo(
              "useGameLoader",
              `Game is in SETUP, calling join endpoint for ${loadedGameId}`
            );
            await gameApi.joinGame(loadedGameId);
            GameLogger.logInfo("useGameLoader", `Successfully joined game ${loadedGameId}`);

            // Set hasJoinedGame after successful join for ALL players
            setHasJoinedGame(true);
          } catch (joinError) {
            GameLogger.logError("useGameLoader", `Failed to join game ${loadedGameId}:`, joinError);
            // Continue anyway - we can still display the game
          }
        } else {
          // Game is not in SETUP, so we can consider it already joined
          setHasJoinedGame(true);
        }

        // For IN_PROGRESS games, gameState comes from SSE, not the initial fetch
        if (gameData.game.gameState) {
          setInitialGameStateInternal(gameData.game.gameState);
        }
        setInitialGameDetailsInternal({
          player1Id: gameData.game.player1Id,
          player2Id: gameData.game.player2Id,
          status: gameData.game.status,
          cubeValue: gameData.game.cubeValue,
          cubeOwner: gameData.game.cubeOwner,
          pendingDoubleFrom: gameData.game.pendingDoubleFrom,
          stakes: gameData.game.stakes,
          turnCount: gameData.game.turnCount,
          doublingAllowed: gameData.game.doublingAllowed,
          currentTurn: gameData.game.currentTurn,
          turnStartedAt: gameData.game.turnStartedAt,
          turnDeadline: gameData.game.turnDeadline,
          timeRemaining: gameData.game.timeRemaining,
          gameMode: gameData.game.gameMode || "standard", // Server and types now properly aligned
          timeBank1: gameData.game.timeBank1,
          timeBank2: gameData.game.timeBank2
        });
        setErrorLoadingInitialData(null);
        setIsLoadingInitialData(false);
      } catch (err) {
        if (!isMounted) return;
        clearLoadingTimeout();
        GameLogger.logError(
          "useGameLoader",
          `Error fetching initial data for game ${loadedGameId}:`,
          err
        );
        setErrorLoadingInitialData(
          err instanceof Error
            ? err.message
            : "An unknown error occurred while fetching initial game details."
        );
        setInitialGameStateInternal(null);
        setInitialGameDetailsInternal({
          player1Id: "",
          player2Id: "",
          status: undefined,
          cubeValue: undefined,
          cubeOwner: undefined,
          pendingDoubleFrom: undefined,
          stakes: undefined,
          turnCount: undefined,
          gameMode: "standard"
        });
        setIsLoadingInitialData(false);
      }
    };

    fetchInitialGameData();

    const loadGamePlayAssets = async () => {
      GameLogger.logInfo("useGameLoader", `Loading game play assets for game: ${loadedGameId}`);

      setIsLoadingGamePlayAssets(true);

      // TODO - load only the data for the intended game theme (board & checkers) and the player specific dice.
      const manifest = {
        bundles: [...CommonManifest.bundles, ...BoardManifest.bundles, ...DiceManifest.bundles]
      };

      const bundlesToLoad = [
        "common-gameplay",
        "boards-default",
        "dice-default-local",
        "dice-default-opponent"
      ];

      const updateProgress = (progress: number) => {
        GameLogger.logDebug("useGameLoader", `Game play asset load progress: ${progress}%`);
      };

      try {
        await Assets.init({ manifest });
        await Assets.loadBundle(bundlesToLoad, updateProgress);

        setLoadedGamePlayAssetBundles(bundlesToLoad);
        setIsLoadingGamePlayAssets(false);
      } catch (error) {
        GameLogger.logError("useGameLoader", "Failed to load game assets:", error);
        setErrorLoadingGamePlayAssets(true);
        setIsLoadingGamePlayAssets(false);
      }
    };

    loadGamePlayAssets();

    return () => {
      isMounted = false;
      clearLoadingTimeout();
      GameLogger.logInfo(
        "useGameLoader",
        `Cleaning up effect for initial data fetch: ${loadedGameId}`
      );
    };
  }, [loadedGameId, gameApi, userId]);

  return {
    loadedGameId,
    initialGameState: initialGameStateInternal,
    initialGameDetails: initialGameDetailsInternal,
    isLoadingInitialData,
    errorLoadingInitialData,
    isLoadingGamePlayAssets,
    errorLoadingGamePlayAssets,
    loadedGamePlayAssetBundles,
    hasJoinedGame
  };
}
